import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../hooks/useWallet';
import { useModalAlert } from '../ModalAlert';
import { AgentAvatar, PALETTES } from '../AgentAvatar';
import { generateAgentSlug, shortUuid, toSlug, SLUG_RE } from '../../utils/agentNames';

// ─── Step constants ────────────────────────────────────────────────────────────
const STEP_PORTFOLIO     = 0;
const STEP_IDENTITY      = 1;
const STEP_STRATEGY      = 2;
const STEP_RESEARCH      = 3;
const STEP_RECOMMENDATION = 4;
const STEP_EXECUTE       = 5;
const STEP_DONE          = 6;

const STEP_LABELS = ['Portfolio', 'Identity', 'Strategy', 'Research', 'Recommend', 'Execute', 'Done'];

function randomPaletteIndex() {
  return Math.floor(Math.random() * PALETTES.length);
}

function makeSeed() {
  const arr = new Uint8Array(6);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}

function encodeSeed(seedStr, paletteIndex) {
  return `${seedStr}|${paletteIndex}`;
}

// ─── Strategy icons ────────────────────────────────────────────────────────────
// Preset id takes precedence; falls back to bucket-based icon below.
const STRATEGY_PRESET_ICONS = {
  'easy-setup': '✨',
};
const STRATEGY_ICONS = {
  MOMENTUM:             '⚡',
  NEW_LISTINGS:         '✦',
  TRENDING_SOCIAL:      '◈',
  FUNDING_RATE_ANOMALY: '⟁',
  VOLUME_BREAKOUT:      '▲',
  WHALE_ACTIVITY:       '◉',
  SECTOR_ROTATION:      '⟳',
  CORRELATION_DIVERGENCE: '⤢',
  LIQUIDATION_CLUSTERS: '⬡',
};

const STRATEGY_COLORS = {
  MOMENTUM:             'from-cyan-900/40 to-cyan-950/40 border-cyan-700/50 hover:border-cyan-500',
  NEW_LISTINGS:         'from-emerald-900/40 to-emerald-950/40 border-emerald-700/50 hover:border-emerald-500',
  TRENDING_SOCIAL:      'from-violet-900/40 to-violet-950/40 border-violet-700/50 hover:border-violet-500',
  FUNDING_RATE_ANOMALY: 'from-amber-900/40 to-amber-950/40 border-amber-700/50 hover:border-amber-500',
  VOLUME_BREAKOUT:      'from-sky-900/40 to-sky-950/40 border-sky-700/50 hover:border-sky-500',
  WHALE_ACTIVITY:       'from-indigo-900/40 to-indigo-950/40 border-indigo-700/50 hover:border-indigo-500',
  SECTOR_ROTATION:      'from-teal-900/40 to-teal-950/40 border-teal-700/50 hover:border-teal-500',
  CORRELATION_DIVERGENCE: 'from-rose-900/40 to-rose-950/40 border-rose-700/50 hover:border-rose-500',
  LIQUIDATION_CLUSTERS: 'from-orange-900/40 to-orange-950/40 border-orange-700/50 hover:border-orange-500',
};

const STRATEGY_SELECTED_COLORS = {
  MOMENTUM:             'border-cyan-400 bg-cyan-900/30 ring-1 ring-cyan-400/40',
  NEW_LISTINGS:         'border-emerald-400 bg-emerald-900/30 ring-1 ring-emerald-400/40',
  TRENDING_SOCIAL:      'border-violet-400 bg-violet-900/30 ring-1 ring-violet-400/40',
  FUNDING_RATE_ANOMALY: 'border-amber-400 bg-amber-900/30 ring-1 ring-amber-400/40',
  VOLUME_BREAKOUT:      'border-sky-400 bg-sky-900/30 ring-1 ring-sky-400/40',
  WHALE_ACTIVITY:       'border-indigo-400 bg-indigo-900/30 ring-1 ring-indigo-400/40',
  SECTOR_ROTATION:      'border-teal-400 bg-teal-900/30 ring-1 ring-teal-400/40',
  CORRELATION_DIVERGENCE: 'border-rose-400 bg-rose-900/30 ring-1 ring-rose-400/40',
  LIQUIDATION_CLUSTERS: 'border-orange-400 bg-orange-900/30 ring-1 ring-orange-400/40',
};

// ─── Step: Pick Portfolio ─────────────────────────────────────────────────────
function StepPortfolio({ paperPortfolios, portfolioDetails, selectedId, onSelect }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400 leading-relaxed">
        Your agent will be assigned to a practice portfolio. Select which one it should trade for.
      </p>

      {/* Paper trading info panel */}
      <div className="flex gap-3 rounded-lg border border-cyan-800/50 bg-cyan-950/30 px-4 py-3">
        <svg className="flex-shrink-0 w-4 h-4 mt-0.5 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <div className="space-y-0.5">
          <p className="text-xs font-semibold text-cyan-300">Paper Trading Portfolio</p>
          <p className="text-xs text-slate-400 leading-relaxed">
            Your first portfolio is a <span className="text-slate-200">risk-free paper trading</span> account — trades use simulated funds so you can test your agent's strategy with no real money on the line. Graduate to a live Hyperliquid portfolio whenever you're ready.
          </p>
        </div>
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {paperPortfolios.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No paper portfolios yet. Create one from the Dashboard first.</p>
        ) : paperPortfolios.map((p) => {
          const detail = portfolioDetails?.get(p.id);
          const pnl = detail?.total_pnl_usd;
          const pnlPct = detail?.total_pnl_pct;
          const agentName = detail?.active_agent?.name || p.active_agent?.name;
          const isSelected = p.id === selectedId;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                isSelected
                  ? 'border-cyan-400 bg-cyan-900/20 ring-1 ring-cyan-400/30'
                  : 'border-slate-700 bg-slate-800/40 hover:border-slate-500 hover:bg-slate-800/60'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-white">{p.name}</span>
                {pnl != null ? (
                  <span className={`text-xs font-mono tabular-nums ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                    {pnlPct != null && ` (${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(1)}%)`}
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {agentName ? `Agent: ${agentName}` : <span className="text-amber-500/80">No agent assigned</span>}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step: Identity ────────────────────────────────────────────────────────────
function StepIdentity({ agentName, setAgentName, nameSuffix, paletteIndex, setPaletteIndex, avatarSeed, setAvatarSeed, busy }) {
  const slug = toSlug(agentName.trim());
  const isValid = SLUG_RE.test(slug) && slug.length > 0;
  const finalName = isValid ? `${slug}-${nameSuffix}` : `…-${nameSuffix}`;

  const cycleAvatar = () => {
    setAvatarSeed(makeSeed());
    setPaletteIndex(randomPaletteIndex());
  };

  return (
    <div className="space-y-5">
      {/* Avatar spotlight */}
      <div className="flex flex-col items-center gap-3">
        <div className="relative">
          <div className={`absolute inset-0 rounded-full blur-xl opacity-40 transition-all duration-500`}
            style={{ background: `radial-gradient(circle, ${PALETTES[paletteIndex][3]}, transparent)` }}
          />
          <div className="relative w-20 h-20 rounded-full ring-2 ring-white/10 overflow-hidden">
            <AgentAvatar
              name={agentName || 'agent'}
              seed={avatarSeed}
              colors={PALETTES[paletteIndex]}
              size={80}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={cycleAvatar}
          disabled={busy}
          className="text-xs text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-1 disabled:opacity-40"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          new look
        </button>
      </div>

      {/* Name input */}
      <div>
        <label className="block text-xs font-medium text-slate-400 uppercase tracking-widest mb-2">Agent name</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            placeholder="e.g. swift-fox"
            maxLength={16}
            disabled={busy}
            className={`flex-1 bg-slate-900 border rounded px-3 py-2 text-sm font-mono text-white focus:outline-none focus:ring-1 disabled:opacity-50 transition-colors ${
              agentName && !isValid ? 'border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-cyan-500 focus:ring-cyan-500/30'
            }`}
          />
          <button
            type="button"
            onClick={() => setAgentName(generateAgentSlug())}
            disabled={busy}
            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded text-slate-300 hover:text-white text-sm transition-colors disabled:opacity-40"
            title="Generate random name"
          >
            🎲
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1.5 font-mono">
          {isValid
            ? <>Full name: <span className="text-slate-300">{finalName}</span></>
            : agentName
              ? <span className="text-red-400">Lowercase letters, numbers, hyphens only</span>
              : 'Enter a slug or click 🎲'}
        </p>
      </div>

      <div className="p-3 bg-slate-800/50 border border-slate-700/50 rounded-lg">
        <p className="text-xs text-slate-400 leading-relaxed">
          We've picked a fast, cost-efficient model for your agent.
          You can change the model later in agent settings.
        </p>
      </div>
    </div>
  );
}

// ─── Step: Strategy ────────────────────────────────────────────────────────────
function StepStrategy({ strategies, selectedPresetId, onSelect, methodologyPrompt, setMethodologyPrompt, isFirstAgent, busy }) {
  const [showCustomize, setShowCustomize] = useState(false);

  const iconFor = (s) => STRATEGY_PRESET_ICONS[s.id] || STRATEGY_ICONS[s.bucket] || '◆';

  const easySetup = strategies.find(s => s.id === 'easy-setup');
  const specialist = strategies.filter(s => !s.featured);

  // ── Mode A: first agent — Easy Setup only ──────────────────────────────────
  if (isFirstAgent) {
    const isSelected = selectedPresetId === 'easy-setup';
    return (
      <div className="space-y-4">
        <div className="p-3 bg-slate-800/60 border border-slate-700/50 rounded-lg">
          <p className="text-xs text-slate-300 leading-relaxed">
            We've chosen the best strategy for your first trade. Easy Setup picks the most liquid markets
            to maximize the chance of a successful first recommendation. You can switch to a specialist
            strategy any time from the workspace.
          </p>
        </div>

        {easySetup && (
          <button
            type="button"
            onClick={() => { onSelect(easySetup); setShowCustomize(false); }}
            disabled={busy}
            className={`relative w-full text-left p-4 rounded-lg border transition-all disabled:opacity-40 ${
              isSelected
                ? 'border-cyan-400 bg-gradient-to-br from-cyan-900/40 to-cyan-950/30 ring-1 ring-cyan-400/50'
                : 'border-cyan-700/60 bg-gradient-to-br from-cyan-950/50 to-slate-900 hover:border-cyan-500'
            }`}
          >
            <div className="flex items-center gap-2 absolute top-3 right-3">
              <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-cyan-500/20 border border-cyan-400/40 text-cyan-300">
                RECOMMENDED
              </span>
              {isSelected && (
                <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-green-500/20 border border-green-400/40 text-green-300">
                  SELECTED
                </span>
              )}
            </div>
            <div className="flex items-start gap-3 pr-32">
              <div className="text-2xl leading-none mt-0.5">{iconFor(easySetup)}</div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-white">{easySetup.label}</div>
                <div className="text-xs text-slate-400 mt-0.5 leading-snug">{easySetup.description}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {['Top liquid markets', 'Guaranteed tokens', 'Best first-trade odds'].map(tag => (
                    <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-900/40 border border-cyan-800/60 text-cyan-400">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </button>
        )}

        {isSelected && (
          <div>
            <button
              type="button"
              onClick={() => setShowCustomize(v => !v)}
              className="text-xs text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-1"
            >
              <svg className={`w-3 h-3 transition-transform ${showCustomize ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Customize methodology prompt
            </button>
            {showCustomize && (
              <textarea
                value={methodologyPrompt}
                onChange={(e) => setMethodologyPrompt(e.target.value)}
                rows={4}
                disabled={busy}
                className="mt-2 w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-xs font-mono text-slate-300 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 resize-none disabled:opacity-50"
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Mode B: returning user — specialist strategies only ────────────────────
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">Choose a research strategy. Your agent will use this to identify opportunities.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-0.5">
        {specialist.map((s) => {
          const isSelected = s.id === selectedPresetId;
          const colors = isSelected
            ? STRATEGY_SELECTED_COLORS[s.bucket] || 'border-cyan-400 bg-cyan-900/30 ring-1 ring-cyan-400/40'
            : `bg-gradient-to-br border ${STRATEGY_COLORS[s.bucket] || 'border-slate-700 hover:border-slate-500'}`;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => { onSelect(s); setShowCustomize(false); }}
              disabled={busy}
              className={`text-left p-3 rounded-lg border transition-all disabled:opacity-40 ${colors}`}
            >
              <div className="text-xl mb-1.5 leading-none">{iconFor(s)}</div>
              <div className="text-xs font-semibold text-white leading-tight">{s.label}</div>
              <div className="text-[10px] text-slate-400 mt-0.5 leading-tight line-clamp-2">{s.description}</div>
            </button>
          );
        })}
      </div>

      {selectedPresetId && (
        <div>
          <button
            type="button"
            onClick={() => setShowCustomize(v => !v)}
            className="text-xs text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-1"
          >
            <svg className={`w-3 h-3 transition-transform ${showCustomize ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Customize methodology prompt
          </button>
          {showCustomize && (
            <textarea
              value={methodologyPrompt}
              onChange={(e) => setMethodologyPrompt(e.target.value)}
              rows={4}
              disabled={busy}
              className="mt-2 w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-xs font-mono text-slate-300 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 resize-none disabled:opacity-50"
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Step: Research ────────────────────────────────────────────────────────────
function StepResearch({ state, result, error, onRun, onSkip, onTryAnotherStrategy, onCompleteAnyway, busy }) {
  const lines = [
    'Initializing research context…',
    'Scanning market microstructure…',
    'Analyzing order flow and funding rates…',
    'Correlating social and on-chain signals…',
    'Scoring token candidates…',
    'Compiling research summary…',
  ];
  const [lineIndex, setLineIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (state === 'running') {
      setLineIndex(0);
      timerRef.current = setInterval(() => {
        setLineIndex(i => (i + 1) % lines.length);
      }, 1800);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [state]); // eslint-disable-line

  if (state === 'idle') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-400 leading-relaxed">
          Your agent will scan the markets using your selected strategy and identify the best tokens to track.
          This typically takes 20–60 seconds.
        </p>
        <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-white">Live market scan</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Research consumes one research run from your plan quota.
            </p>
          </div>
        </div>
        {error && (
          <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">{error}</div>
        )}
      </div>
    );
  }

  if (state === 'running') {
    return (
      <div className="space-y-4">
        <div className="font-mono text-xs bg-slate-950 border border-slate-700 rounded-lg p-4 min-h-24 flex flex-col justify-between">
          <div className="space-y-1">
            {lines.slice(0, lineIndex + 1).map((l, i) => (
              <div key={i} className={`flex items-center gap-2 ${i === lineIndex ? 'text-cyan-400' : 'text-slate-500'}`}>
                <span className="text-slate-600">{'>'}</span>
                <span>{l}</span>
                {i === lineIndex && <span className="inline-block w-1.5 h-3 bg-cyan-400 animate-pulse" />}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-500 text-center animate-pulse">Consulting markets…</p>
      </div>
    );
  }

  if (state === 'done' && result) {
    const tokens = result.proposed_tokens || [];

    // Zero-token result — distinct empty state with recovery paths
    if (tokens.length === 0) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/50 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-white">No tokens found</span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            The research completed but didn't identify any qualifying tokens for the <strong className="text-slate-300">{result.bucket?.toLowerCase().replace(/_/g, ' ')}</strong> strategy right now. Market conditions may not match this strategy's criteria.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onTryAnotherStrategy}
              className="w-full px-4 py-2.5 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-colors"
            >
              ← Try a different strategy
            </button>
            <button
              type="button"
              onClick={onCompleteAnyway}
              className="w-full px-4 py-2.5 bg-slate-800/60 border border-slate-700 text-slate-400 text-sm rounded-lg hover:text-slate-200 transition-colors"
            >
              Complete onboarding without tokens
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center flex-shrink-0">
            <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-white">Research complete</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {tokens.map(t => (
            <span key={t} className="px-2 py-0.5 text-xs font-mono font-semibold rounded bg-cyan-900/40 border border-cyan-700/50 text-cyan-300">{t}</span>
          ))}
        </div>
        {result.summary_markdown && (
          <div className="max-h-32 overflow-y-auto p-3 bg-slate-900/60 border border-slate-700/50 rounded text-xs text-slate-400 leading-relaxed whitespace-pre-wrap font-mono">
            {result.summary_markdown.slice(0, 600)}{result.summary_markdown.length > 600 ? '…' : ''}
          </div>
        )}
        {error && (
          <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">{error}</div>
        )}
      </div>
    );
  }

  if (state === 'error') {
    const isLimitError = error?.includes('limit reached') || error?.includes('limit_count');
    return (
      <div className="space-y-3">
        <div className={`p-3 rounded text-sm leading-relaxed ${
          isLimitError
            ? 'bg-amber-900/30 border border-amber-700/50 text-amber-200'
            : 'bg-red-900/30 border border-red-700/50 text-red-300'
        }`}>
          {isLimitError ? (
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
            </div>
          ) : error || 'Research failed.'}
        </div>
        {!isLimitError && (
          <p className="text-xs text-slate-500">You can retry or skip — your agent will still be ready. Run research manually from the workspace later.</p>
        )}
      </div>
    );
  }

  return null;
}

// ─── Step: Recommendation ──────────────────────────────────────────────────────
const REC_SAYINGS = [
  'Analyzing Macroeconomic Trends and Crypto Correlations',
  'Evaluating Total Value Locked (TVL) and Ecosystem Growth',
  'Comparing Layer 1 and Layer 2 Protocols',
  'Monitoring Social Sentiment and Fear & Greed Indices',
  'Analyzing Tokenomics and Vesting Schedules',
  'Assessing Regulatory Compliance and Legal Shifts',
  'Evaluating Network Effects and Developer Activity',
  'Auditing Bridge Security and Oracle Dependencies',
  'Tracking Emerging Crypto Narratives and Tech Cycles',
  'Benchmarking On-Chain Metrics and Transaction Volumes',
];

function RotatingSaying() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(i => (i + 1) % REC_SAYINGS.length);
        setVisible(true);
      }, 400);
    }, 3600);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-[2.5rem] flex items-center justify-center px-4">
      <p
        className="text-sm text-center font-mono text-cyan-300/80 transition-opacity duration-400 leading-snug"
        style={{ opacity: visible ? 1 : 0 }}
      >
        {REC_SAYINGS[index]}
      </p>
    </div>
  );
}

function StepRecommendation({ state, recommendation, error, onRun, busy }) {
  if (state === 'idle') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-400 leading-relaxed">
          Your agent will analyze the research and generate its first trade recommendation.
        </p>
        {error && (
          <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">{error}</div>
        )}
      </div>
    );
  }

  if (state === 'running') {
    return (
      <div className="flex flex-col items-center gap-5 py-3">
        {/* Orbital spinner */}
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full border-2 border-slate-700" />
          <div className="absolute inset-0 rounded-full border-2 border-cyan-500/30 border-t-cyan-400 animate-spin" />
          <div className="absolute inset-2 rounded-full border border-slate-600/50 border-t-cyan-500/40 animate-spin"
            style={{ animationDuration: '1.8s', animationDirection: 'reverse' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
          </div>
        </div>

        {/* Rotating analytical sayings */}
        <RotatingSaying />

        <p className="text-[10px] text-slate-600 uppercase tracking-widest">
          This may take a minute — do not close
        </p>
      </div>
    );
  }

  if (state === 'done' && recommendation) {
    const action = recommendation.action || 'HOLD';
    const isTrade = action !== 'HOLD';
    const actionColors = {
      BUY:   'bg-green-900/40 border-green-600/50 text-green-300',
      SHORT: 'bg-red-900/40 border-red-600/50 text-red-300',
      CLOSE: 'bg-amber-900/40 border-amber-600/50 text-amber-300',
      COVER: 'bg-amber-900/40 border-amber-600/50 text-amber-300',
      HOLD:  'bg-slate-800/60 border-slate-600/50 text-slate-400',
    };
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className={`px-2.5 py-1 text-xs font-bold rounded border uppercase tracking-widest ${actionColors[action] || actionColors.HOLD}`}>
            {action}
          </span>
          {recommendation.pair && (
            <span className="text-sm font-mono font-semibold text-white">{recommendation.pair}</span>
          )}
          {recommendation.confidence != null && (
            <span className="text-xs text-slate-400 ml-auto">{Math.round(recommendation.confidence * 100)}% confidence</span>
          )}
        </div>
        {recommendation.reasoning && (
          <p className="text-xs text-slate-400 leading-relaxed line-clamp-4">{recommendation.reasoning}</p>
        )}
        {error && (
          <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">{error}</div>
        )}
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">{error || 'Recommendation failed. You can continue — your agent is ready and will generate recommendations from the workspace.'}</div>
    );
  }

  return null;
}

// ─── Step: Done ────────────────────────────────────────────────────────────────
// ─── Step: Execute Trade ──────────────────────────────────────────────────────
function StepExecute({ recommendation, executeState, executeResult, executeError, onExecute, onSkip }) {
  const action = recommendation?.action || 'BUY';
  const isBuy = action === 'BUY';
  const pair = recommendation?.pair || '—';
  const confidence = recommendation?.confidence != null ? Math.round(recommendation.confidence * 100) : null;
  const posSize = recommendation?.recommended_position_size_usd;
  const leverage = recommendation?.leverage ?? 2;
  const stopPct = recommendation?.stop_loss_pct;
  const tpPct = recommendation?.take_profit_pct;
  const rrRatio = recommendation?.risk_reward_ratio;

  const actionGradient = isBuy
    ? 'from-emerald-900/60 to-emerald-950/50 border-emerald-600/60'
    : 'from-red-900/60 to-red-950/50 border-red-600/60';
  const actionBadge = isBuy
    ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300'
    : 'bg-red-500/20 border-red-500 text-red-300';
  const executeBtnBase = isBuy
    ? 'bg-emerald-600 hover:bg-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.25)] hover:shadow-[0_0_28px_rgba(16,185,129,0.4)]'
    : 'bg-red-600 hover:bg-red-500 shadow-[0_0_20px_rgba(239,68,68,0.25)] hover:shadow-[0_0_28px_rgba(239,68,68,0.4)]';

  if (executeState === 'done') {
    return (
      <div className="flex flex-col items-center gap-4 py-3">
        <div className="relative">
          <div className={`absolute inset-0 rounded-full blur-xl opacity-50 animate-pulse ${isBuy ? 'bg-emerald-500/30' : 'bg-red-500/30'}`} />
          <div className={`relative w-14 h-14 rounded-full border-2 flex items-center justify-center ${isBuy ? 'bg-emerald-900/40 border-emerald-400' : 'bg-red-900/40 border-red-400'}`}>
            <svg className={`w-7 h-7 ${isBuy ? 'text-emerald-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <div className="text-center">
          <p className="text-base font-bold text-white">Trade placed!</p>
          <p className="text-xs text-slate-400 mt-1">{isBuy ? 'Long' : 'Short'} {pair} · {leverage}x leverage</p>
          {executeResult?.message && (
            <p className="text-xs text-slate-500 mt-1 max-w-xs">{executeResult.message}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">Review the order details and execute your first trade to complete onboarding.</p>

      {/* Order ticket */}
      <div className={`rounded-lg border bg-gradient-to-br p-4 ${actionGradient}`}>
        {/* Action header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <span className={`px-3 py-1 text-sm font-black rounded border uppercase tracking-[0.12em] ${actionBadge}`}>
              {action}
            </span>
            <span className="text-lg font-bold font-mono text-white">{pair}</span>
          </div>
          <div className="text-right">
            {confidence != null && (
              <p className="text-xs text-slate-400 font-mono">{confidence}% confidence</p>
            )}
            <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wide">Paper trade</p>
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Leverage', value: `${leverage}×` },
            { label: 'Stop Loss', value: stopPct != null ? `-${stopPct}%` : '—' },
            { label: 'Take Profit', value: tpPct != null ? `+${tpPct}%` : '—' },
            { label: 'Position', value: posSize != null ? `$${Math.round(posSize).toLocaleString()}` : '—' },
            { label: 'R:R Ratio', value: rrRatio != null ? `${rrRatio}:1` : '—' },
            { label: 'Type', value: isBuy ? 'Long' : 'Short' },
          ].map(({ label, value }) => (
            <div key={label} className="bg-slate-900/60 rounded p-2 text-center">
              <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">{label}</p>
              <p className="text-xs font-semibold font-mono text-slate-200">{value}</p>
            </div>
          ))}
        </div>

        {/* Reasoning preview */}
        {recommendation?.reasoning && (
          <p className="mt-3 text-[10px] text-slate-400/80 leading-relaxed line-clamp-2 border-t border-slate-700/40 pt-2.5">
            {recommendation.reasoning.replace(/^First-trade Easy Setup\.\s*/i, '')}
          </p>
        )}
      </div>

      {executeState === 'executing' && (
        <div className="flex items-center justify-center gap-2 py-1">
          <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          <span className="text-xs text-slate-400">Placing order…</span>
        </div>
      )}

      {executeError && (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-xs text-red-300">{executeError}</div>
      )}
    </div>
  );
}

const NEXT_STEPS = [
  {
    num: '01',
    accent: 'border-cyan-500/60 bg-cyan-950/40',
    numColor: 'text-cyan-400',
    titleColor: 'text-cyan-200',
    icon: (
      <svg className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Tune research strategy',
    body: 'Switch buckets or edit the methodology prompt to find setups that match your market view.',
    tab: 'setup',
  },
  {
    num: '02',
    accent: 'border-amber-500/60 bg-amber-950/30',
    numColor: 'text-amber-400',
    titleColor: 'text-amber-200',
    icon: (
      <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    title: 'Customise recommendation prompt',
    body: 'Use one of our strategy presets or craft your own prompt to shape how trades are evaluated.',
    tab: 'setup',
  },
  {
    num: '03',
    accent: 'border-emerald-500/60 bg-emerald-950/30',
    numColor: 'text-emerald-400',
    titleColor: 'text-emerald-200',
    icon: (
      <svg className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    title: 'Build your win record',
    body: 'Run more research cycles and let your agent accumulate trades. Patterns emerge over time.',
    tab: 'performance',
  },
];

function NextStepsPanel({ portfolioId }) {
  const navigate = useNavigate();
  return (
    <div className="w-full mt-2 space-y-2">
      <p className="text-xs text-slate-500 uppercase tracking-widest font-medium mb-3">What to do next</p>
      {NEXT_STEPS.map((step, i) => (
        <button
          key={step.num}
          type="button"
          onClick={() => navigate(`/workspace/${portfolioId}?tab=${step.tab}`)}
          style={{ animationDelay: `${i * 80}ms` }}
          className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition-all hover:brightness-110 active:scale-[0.99] ${step.accent}`}
        >
          <span className={`text-[10px] font-bold font-mono mt-0.5 w-5 flex-shrink-0 ${step.numColor}`}>{step.num}</span>
          <div className="min-w-0 flex-1">
            <div className={`flex items-center gap-2 mb-0.5`}>
              {step.icon}
              <span className={`text-xs font-semibold ${step.titleColor}`}>{step.title}</span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">{step.body}</p>
          </div>
          <svg className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ))}
    </div>
  );
}

function StepDone({ tradeMade, researchSkipped, agentName, portfolioName, portfolioId, onGoTo }) {
  if (researchSkipped) {
    return (
      <div className="flex flex-col items-center text-center space-y-4 py-2">
        <div className="w-16 h-16 rounded-full bg-teal-900/30 border-2 border-teal-500/60 flex items-center justify-center">
          <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Your agent is ready.</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-xs">
            Run research from the workspace whenever you're ready — your agent will start producing recommendations automatically.
          </p>
        </div>
        {agentName && <p className="text-xs font-mono text-slate-500">{agentName} → {portfolioName || `portfolio #${portfolioId}`}</p>}
        {portfolioId && <NextStepsPanel portfolioId={portfolioId} />}
      </div>
    );
  }

  if (tradeMade === false) {
    return (
      <div className="flex flex-col items-center text-center space-y-4 py-2">
        <div className="w-16 h-16 rounded-full bg-slate-800/60 border-2 border-slate-500/60 flex items-center justify-center">
          <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Holding for now.</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-xs leading-relaxed">
            Your agent analyzed the market and recommended holding. Conditions weren't right for a trade — explore below to sharpen your strategy.
          </p>
        </div>
        {agentName && <p className="text-xs font-mono text-slate-500">{agentName} → {portfolioName || `portfolio #${portfolioId}`}</p>}
        {portfolioId && <NextStepsPanel portfolioId={portfolioId} />}
      </div>
    );
  }

  if (tradeMade === true) {
    return (
      <div className="flex flex-col items-center text-center space-y-4 py-2">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-green-500/20 blur-xl animate-pulse" />
          <div className="relative w-16 h-16 rounded-full bg-green-900/40 border-2 border-green-400 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">First trade is live!</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-xs leading-relaxed">
            You've made a trade — now make your agent a powerful trading assistant by exploring these next steps.
          </p>
        </div>
        {agentName && <p className="text-xs font-mono text-slate-500">{agentName} → {portfolioName || `portfolio #${portfolioId}`}</p>}
        {portfolioId && <NextStepsPanel portfolioId={portfolioId} />}
      </div>
    );
  }

  // Default / recommendation errored
  return (
    <div className="flex flex-col items-center text-center space-y-4 py-2">
      <div className="w-16 h-16 rounded-full bg-cyan-900/30 border-2 border-cyan-500/60 flex items-center justify-center">
        <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-bold text-white">Agent is set up!</h3>
        <p className="text-sm text-slate-400 mt-1 max-w-xs">
          Head to the workspace to review research results and run your first recommendation.
        </p>
      </div>
      {agentName && <p className="text-xs font-mono text-slate-500">{agentName} → {portfolioName || `portfolio #${portfolioId}`}</p>}
      {portfolioId && <NextStepsPanel portfolioId={portfolioId} />}
    </div>
  );
}

// ─── Step Indicator ────────────────────────────────────────────────────────────
function StepIndicator({ step, hasPortfolioCtx }) {
  const visibleSteps = hasPortfolioCtx
    ? [STEP_IDENTITY, STEP_STRATEGY, STEP_RESEARCH, STEP_RECOMMENDATION, STEP_EXECUTE, STEP_DONE]
    : [STEP_PORTFOLIO, STEP_IDENTITY, STEP_STRATEGY, STEP_RESEARCH, STEP_RECOMMENDATION, STEP_EXECUTE, STEP_DONE];
  const labels = hasPortfolioCtx
    ? ['Identity', 'Strategy', 'Research', 'Rec.', 'Execute', 'Done']
    : ['Portfolio', 'Identity', 'Strategy', 'Research', 'Rec.', 'Execute', 'Done'];

  return (
    <div className="flex items-center gap-0.5 mb-5">
      {visibleSteps.map((s, i) => {
        const done = step > s;
        const active = step === s;
        return (
          <div key={s} className="flex items-center flex-1">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all flex-shrink-0 ${
              done ? 'bg-cyan-600 text-white' :
              active ? 'bg-cyan-500 text-white ring-2 ring-cyan-500/30' :
              'bg-slate-800 border border-slate-700 text-slate-500'
            }`}>
              {done ? '✓' : i + 1}
            </div>
            {i < visibleSteps.length - 1 && (
              <div className={`flex-1 h-px mx-0.5 transition-colors ${done ? 'bg-cyan-700' : 'bg-slate-700'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────
export function AgentOnboardWizard({ isOpen, onClose, portfolioId: portfolioIdProp, paperPortfolios, portfolioDetails, config, onCompleted, existingAgentCount = 0 }) {
  // true when no agents existed when the wizard opened — only show Easy Setup
  const isFirstAgent = existingAgentCount === 0;
  const navigate = useNavigate();
  const { jwtToken } = useWallet();
  const { showError } = useModalAlert();

  const hasPortfolioCtx = !!portfolioIdProp;
  const initialStep = hasPortfolioCtx ? STEP_IDENTITY : STEP_PORTFOLIO;

  const [step, setStep] = useState(initialStep);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(portfolioIdProp || null);
  const [selectedPortfolioName, setSelectedPortfolioName] = useState(null);

  // Identity step state
  const [agentName, setAgentName] = useState(generateAgentSlug);
  const [nameSuffix] = useState(shortUuid);
  const [paletteIndex, setPaletteIndex] = useState(randomPaletteIndex);
  const [avatarSeed, setAvatarSeed] = useState(makeSeed);

  // Created entity refs
  const [createdAgent, setCreatedAgent] = useState(null);

  // Strategy step state
  const [strategyCatalog, setStrategyCatalog] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);  // full preset object
  const [methodologyPrompt, setMethodologyPrompt] = useState('');

  // Research step state
  const [researchState, setResearchState] = useState('idle'); // idle | running | done | error
  const [researchResult, setResearchResult] = useState(null);
  const [researchHasTokens, setResearchHasTokens] = useState(null); // null | true | false
  const [researchSkipped, setResearchSkipped] = useState(false);

  // Recommendation step state
  const [recState, setRecState] = useState('idle'); // idle | running | done | error
  const [recommendation, setRecommendation] = useState(null);
  const [tradeMade, setTradeMade] = useState(null);

  // Execute step state
  const [executeState, setExecuteState] = useState('idle'); // idle | executing | done | error
  const [executeResult, setExecuteResult] = useState(null);
  const [executeError, setExecuteError] = useState(null);

  const [busy, setBusy] = useState(false);
  const [stepError, setStepError] = useState(null);

  const pollRef = useRef(null);
  const apiBase = config?.apiBaseUrl || 'http://localhost:8000';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` };

  // Fetch strategy catalog when strategy step is shown
  useEffect(() => {
    if (!isOpen || strategyCatalog.length > 0) return;
    fetch(`${apiBase}/research/strategies`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const strategies = data.strategies || [];
        setStrategyCatalog(strategies);
        // Auto-select Easy Setup for first-time agents
        if (isFirstAgent) {
          const easySetup = strategies.find(s => s.id === 'easy-setup');
          if (easySetup && !selectedPreset) {
            setSelectedPreset(easySetup);
            setMethodologyPrompt(easySetup.default_methodology_prompt);
          }
        }
      })
      .catch(() => {});
  }, [isOpen, step, strategyCatalog.length, apiBase]); // eslint-disable-line

  // Reset state when wizard closes
  useEffect(() => {
    if (!isOpen) {
      setStep(hasPortfolioCtx ? STEP_IDENTITY : STEP_PORTFOLIO);
      setSelectedPortfolioId(portfolioIdProp || null);
      setSelectedPortfolioName(null);
      setAgentName(generateAgentSlug());
      setPaletteIndex(randomPaletteIndex());
      setAvatarSeed(makeSeed());
      setCreatedAgent(null);
      setSelectedPreset(null);
      setMethodologyPrompt('');
      setResearchState('idle');
      setResearchResult(null);
      setResearchHasTokens(null);
      setResearchSkipped(false);
      setRecState('idle');
      setRecommendation(null);
      setTradeMade(null);
      setExecuteState('idle');
      setExecuteResult(null);
      setExecuteError(null);
      setBusy(false);
      setStepError(null);
      clearInterval(pollRef.current);
    }
  }, [isOpen]); // eslint-disable-line

  // ── Step transitions ──────────────────────────────────────────────────────

  const handlePortfolioContinue = () => {
    if (!selectedPortfolioId) { setStepError('Please select a portfolio.'); return; }
    setStepError(null);
    setStep(STEP_IDENTITY);
  };

  const handleIdentityContinue = async () => {
    const slug = toSlug(agentName.trim());
    if (!SLUG_RE.test(slug)) { setStepError('Agent name is invalid.'); return; }
    setStepError(null);
    setBusy(true);
    let newAgentId = null;
    try {
      const finalName = `${slug}-${nameSuffix}`;
      const createRes = await fetch(`${apiBase}/agents`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: finalName, max_history_messages: 10 }),
      });
      if (!createRes.ok) throw new Error(`Create failed (${createRes.status})`);
      const agent = await createRes.json();
      newAgentId = agent.id;

      // Persist avatar seed
      await fetch(`${apiBase}/agents/${agent.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ avatar_seed: encodeSeed(avatarSeed, paletteIndex) }),
      }).catch(() => {});

      // Assign to portfolio
      const assignRes = await fetch(`${apiBase}/portfolio/${selectedPortfolioId}/agent`, {
        method: 'POST', headers,
        body: JSON.stringify({ agent_id: agent.id, role: 'primary' }),
      });
      if (!assignRes.ok) {
        // Rollback
        await fetch(`${apiBase}/agents/${agent.id}`, { method: 'DELETE', headers }).catch(() => {});
        throw new Error(`Assignment failed (${assignRes.status})`);
      }

      setCreatedAgent({ ...agent, avatar_seed: encodeSeed(avatarSeed, paletteIndex) });
      setStep(STEP_STRATEGY);
    } catch (err) {
      setStepError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleStrategySelect = (preset) => {
    setSelectedPreset(preset);
    setMethodologyPrompt(preset.default_methodology_prompt);
    setStepError(null);
  };

  const handleStrategyContinue = async () => {
    if (!selectedPreset) { setStepError('Please select a strategy.'); return; }
    if (!methodologyPrompt.trim()) { setStepError('Methodology prompt cannot be empty.'); return; }
    setStepError(null);
    setBusy(true);
    try {
      // 1. Create/activate research config
      const res = await fetch(`${apiBase}/agents/${createdAgent.id}/research/config`, {
        method: 'POST', headers,
        body: JSON.stringify({
          bucket: selectedPreset.bucket,
          methodology_prompt: methodologyPrompt.trim(),
          is_active: true,
        }),
      });
      if (!res.ok) throw new Error(`Config failed (${res.status})`);
      const cfg = await res.json();

      // 2. If preset ships a recommendation prompt, create & activate it on the portfolio
      if (selectedPreset.recommendation_prompt) {
        const promptName = `${selectedPreset.id}-${Date.now().toString(36).slice(-6)}`;
        const promptRes = await fetch(`${apiBase}/portfolio/${selectedPortfolioId}/prompts`, {
          method: 'POST', headers,
          body: JSON.stringify({
            name: promptName,
            prompt_text: selectedPreset.recommendation_prompt,
            is_active: true,
            version_tag: 'v1',
          }),
        });
        if (!promptRes.ok) {
          // Non-fatal — the default prompt is still active; log and continue
          console.warn(`Could not install preset recommendation prompt (${promptRes.status})`);
        }
      }

      setCreatedAgent(a => ({ ...a, researchConfigId: cfg.id }));
      setStep(STEP_RESEARCH);
    } catch (err) {
      setStepError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRunResearch = async () => {
    if (!createdAgent?.researchConfigId) return;
    setResearchState('running');
    setStepError(null);
    try {
      const runRes = await fetch(`${apiBase}/portfolio/${selectedPortfolioId}/research/run`, {
        method: 'POST', headers,
        body: JSON.stringify({ research_config_id: createdAgent.researchConfigId }),
      });
      if (!runRes.ok) {
        const errData = await runRes.json().catch(() => ({}));
        const detail = errData.detail;
        let errMsg;
        if (detail && typeof detail === 'object') {
          if (detail.message === 'Research limit reached') {
            errMsg = `Research limit reached — ${detail.usage_count} of ${detail.limit_count} runs used on your current plan. You can skip research and complete onboarding, then run research manually later.`;
          } else {
            errMsg = detail.message || JSON.stringify(detail);
          }
        } else {
          errMsg = detail || `Research failed (${runRes.status})`;
        }
        setResearchState('error');
        setStepError(errMsg);
        return;
      }
      const { ticket_id } = await runRes.json();

      // Poll until done
      let elapsed = 0;
      pollRef.current = setInterval(async () => {
        elapsed += 2000;
        if (elapsed > 90000) { clearInterval(pollRef.current); setResearchState('error'); setStepError('Research timed out.'); return; }
        try {
          const pollRes = await fetch(`${apiBase}/portfolio/${selectedPortfolioId}/research/poll/${ticket_id}`, { headers });
          if (!pollRes.ok) return;
          const poll = await pollRes.json();
          if (!poll.done) return;
          clearInterval(pollRef.current);
          if (poll.error || poll.status === 'FAILED') {
            setResearchState('error');
            setStepError(poll.error || 'Research run failed.');
            return;
          }
          const result = poll.response;
          // Auto-apply tokens; capture how many were actually applied
          let tokensApplied = result.proposed_tokens?.length ?? 0;
          try {
            const applyRes = await fetch(`${apiBase}/portfolio/${selectedPortfolioId}/research/results/${result.id}/apply`, {
              method: 'POST', headers, body: JSON.stringify({}),
            });
            if (applyRes.ok) {
              const applyData = await applyRes.json();
              tokensApplied = applyData.tokens_applied?.length ?? tokensApplied;
            }
          } catch { /* apply failure is non-fatal */ }
          setResearchHasTokens(tokensApplied > 0);
          setResearchResult(result);
          setResearchState('done');
        } catch { /* continue polling */ }
      }, 2000);
    } catch (err) {
      setResearchState('error');
      setStepError(err.message);
    }
  };

  const handleSkipResearch = () => {
    clearInterval(pollRef.current);
    setResearchSkipped(true);
    setStep(STEP_DONE);
  };

  const handleTryAnotherStrategy = () => {
    setResearchState('idle');
    setResearchResult(null);
    setResearchHasTokens(null);
    setStepError(null);
    setSelectedPreset(null);
    setMethodologyPrompt('');
    setStep(STEP_STRATEGY);
  };

  const handleCompleteAnyway = () => {
    setResearchSkipped(true);
    setStep(STEP_DONE);
  };

  const handleResearchNext = () => {
    setStep(STEP_RECOMMENDATION);
  };

  const handleRunRecommendation = async () => {
    setRecState('running');
    setStepError(null);
    try {
      const runRes = await fetch(`${apiBase}/agent/recommendation/message`, {
        method: 'POST', headers,
        body: JSON.stringify({
          message: 'Produce your first recommendation using the most recent research.',
          portfolio_id: parseInt(selectedPortfolioId, 10),
          agent_id: createdAgent.id,
        }),
      });
      if (!runRes.ok) {
        const errData = await runRes.json().catch(() => ({}));
        throw new Error(errData.detail || `Recommendation failed (${runRes.status})`);
      }
      const { ticket_id } = await runRes.json();

      pollRef.current = setInterval(async () => {
        try {
          const pollRes = await fetch(`${apiBase}/agent/recommendation/poll/${ticket_id}`, { headers });
          if (!pollRes.ok) return;
          const poll = await pollRes.json();
          if (!poll.done) return;
          clearInterval(pollRef.current);
          if (poll.error || poll.status === 'FAILED') {
            setRecState('error');
            setStepError(poll.error || 'Recommendation failed.');
            return;
          }
          // Fetch the newest recommendation to get structured data
          const recListRes = await fetch(`${apiBase}/portfolio/${selectedPortfolioId}/recommendation`, { headers });
          if (recListRes.ok) {
            const recs = await recListRes.json();
            const newest = Array.isArray(recs) ? recs[0] : null;
            if (newest) {
              setRecommendation(newest);
              setTradeMade(newest.action !== 'HOLD');
            }
          }
          setRecState('done');
        } catch { /* continue polling */ }
      }, 2000);
    } catch (err) {
      setRecState('error');
      setStepError(err.message);
    }
  };

  const handleRecNext = () => {
    // Route to execute step when a real trade was recommended; skip to done on HOLD
    if (tradeMade === true && recommendation) {
      setStep(STEP_EXECUTE);
    } else {
      setStep(STEP_DONE);
    }
  };

  const handleExecuteTrade = async () => {
    if (!recommendation?.id) return;
    setExecuteState('executing');
    setExecuteError(null);
    try {
      const resp = await fetch(`${apiBase}/portfolio/${selectedPortfolioId}/execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ recommendation_id: recommendation.id, execute_onchain: false }),
      });
      const result = await resp.json();
      if (!resp.ok || result.success === false) {
        throw new Error(result.error || result.message || result.detail || `Execute failed (${resp.status})`);
      }
      setExecuteResult(result);
      setExecuteState('done');
    } catch (err) {
      setExecuteState('error');
      setExecuteError(err.message);
    }
  };

  const handleExecuteNext = () => {
    setStep(STEP_DONE);
  };

  const handleClose = () => {
    clearInterval(pollRef.current);
    if (step === STEP_DONE && onCompleted) {
      onCompleted({ agent: createdAgent, portfolioId: selectedPortfolioId, tradeMade });
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // ── Step title ──────────────────────────────────────────────────────────────
  const stepTitles = {
    [STEP_PORTFOLIO]:      'Pick a Portfolio',
    [STEP_IDENTITY]:       'Meet Your Agent',
    [STEP_STRATEGY]:       'Choose a Strategy',
    [STEP_RESEARCH]:       'First Research Run',
    [STEP_RECOMMENDATION]: 'First Recommendation',
    [STEP_EXECUTE]:        'Execute First Trade',
    [STEP_DONE]:           'Agent Ready',
  };

  // ── Back logic ──────────────────────────────────────────────────────────────
  // Back is not allowed from execute (the order is pending) or done.
  const canGoBack = step > initialStep && step < STEP_EXECUTE && !busy;
  const handleBack = () => {
    setStepError(null);
    if (step === STEP_IDENTITY) setStep(STEP_PORTFOLIO);
    else if (step === STEP_STRATEGY) setStep(STEP_IDENTITY);
    else if (step === STEP_RESEARCH) setStep(STEP_STRATEGY);
    else if (step === STEP_RECOMMENDATION) setStep(STEP_RESEARCH);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-lg bg-slate-900 rounded-xl shadow-2xl border border-slate-700/80 my-4 sm:my-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-700/60">
          <div>
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Agent Onboarding</p>
            <h3 className="text-base font-bold text-white">{stepTitles[step]}</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={busy}
            className="p-1.5 hover:bg-slate-700/60 rounded-lg transition-colors disabled:opacity-40"
          >
            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pt-5 pb-2">
          <StepIndicator step={step} hasPortfolioCtx={hasPortfolioCtx} />

          {step === STEP_PORTFOLIO && (
            <StepPortfolio
              paperPortfolios={paperPortfolios || []}
              portfolioDetails={portfolioDetails}
              selectedId={selectedPortfolioId}
              onSelect={(p) => { setSelectedPortfolioId(p.id); setSelectedPortfolioName(p.name); setStepError(null); }}
            />
          )}

          {step === STEP_IDENTITY && (
            <StepIdentity
              agentName={agentName}
              setAgentName={setAgentName}
              nameSuffix={nameSuffix}
              paletteIndex={paletteIndex}
              setPaletteIndex={setPaletteIndex}
              avatarSeed={avatarSeed}
              setAvatarSeed={setAvatarSeed}
              busy={busy}
            />
          )}

          {step === STEP_STRATEGY && (
            <StepStrategy
              strategies={strategyCatalog}
              selectedPresetId={selectedPreset?.id}
              onSelect={handleStrategySelect}
              methodologyPrompt={methodologyPrompt}
              setMethodologyPrompt={setMethodologyPrompt}
              isFirstAgent={isFirstAgent}
              busy={busy}
            />
          )}

          {step === STEP_RESEARCH && (
            <StepResearch
              state={researchState}
              result={researchResult}
              error={stepError}
              onRun={handleRunResearch}
              onSkip={handleSkipResearch}
              onTryAnotherStrategy={handleTryAnotherStrategy}
              onCompleteAnyway={handleCompleteAnyway}
              busy={busy}
            />
          )}

          {step === STEP_RECOMMENDATION && (
            <StepRecommendation
              state={recState}
              recommendation={recommendation}
              error={stepError}
              onRun={handleRunRecommendation}
              busy={busy}
            />
          )}

          {step === STEP_EXECUTE && (
            <StepExecute
              recommendation={recommendation}
              executeState={executeState}
              executeResult={executeResult}
              executeError={executeError}
              onExecute={handleExecuteTrade}
              onSkip={handleExecuteNext}
            />
          )}

          {step === STEP_DONE && (
            <StepDone
              tradeMade={tradeMade}
              researchSkipped={researchSkipped}
              agentName={createdAgent?.name}
              portfolioName={selectedPortfolioName}
              portfolioId={selectedPortfolioId}
            />
          )}

          {stepError && step !== STEP_RESEARCH && step !== STEP_RECOMMENDATION && step !== STEP_EXECUTE && (
            <div className="mt-3 p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">{stepError}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-slate-700/60 mt-4">
          <div>
            {canGoBack ? (
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
              >
                ← Back
              </button>
            ) : step !== STEP_DONE ? (
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {/* Research step: Skip + Run */}
            {step === STEP_RESEARCH && researchState === 'idle' && (
              <>
                <button type="button" onClick={handleSkipResearch} className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2">
                  Skip for now
                </button>
                <button type="button" onClick={handleRunResearch} disabled={busy}
                  className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md disabled:opacity-50 transition-colors">
                  Run Research
                </button>
              </>
            )}
            {step === STEP_RESEARCH && researchState === 'running' && (
              <button type="button" onClick={handleSkipResearch} className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2">
                Skip
              </button>
            )}
            {step === STEP_RESEARCH && researchState === 'done' && researchHasTokens && (
              <button type="button" onClick={handleResearchNext}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md transition-colors">
                Next: First Recommendation →
              </button>
            )}
            {step === STEP_RESEARCH && researchState === 'error' && (
              <>
                <button type="button" onClick={handleSkipResearch}
                  className="px-4 py-2 text-sm font-semibold text-white bg-cyan-700 hover:bg-cyan-600 rounded-md transition-colors">
                  Skip and continue →
                </button>
                {!stepError?.includes('limit reached') && (
                  <button type="button" onClick={() => { setResearchState('idle'); setStepError(null); }}
                    className="px-4 py-2 text-sm font-medium text-white bg-slate-700 hover:bg-slate-600 rounded-md transition-colors border border-slate-600">
                    Retry
                  </button>
                )}
              </>
            )}

            {/* Recommendation step */}
            {step === STEP_RECOMMENDATION && recState === 'idle' && (
              <button type="button" onClick={handleRunRecommendation} disabled={busy}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md disabled:opacity-50 transition-colors">
                Get Recommendation
              </button>
            )}
            {step === STEP_RECOMMENDATION && recState === 'done' && (
              <button type="button" onClick={handleRecNext}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md transition-colors">
                Continue →
              </button>
            )}
            {step === STEP_RECOMMENDATION && recState === 'error' && (
              <>
                <button type="button" onClick={handleRecNext} className="text-xs text-slate-500 hover:text-slate-300 px-2 transition-colors">
                  Skip
                </button>
                <button type="button" onClick={() => { setRecState('idle'); setStepError(null); }}
                  className="px-4 py-2 text-sm font-medium text-white bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-md transition-colors">
                  Retry
                </button>
              </>
            )}

            {/* Execute step */}
            {step === STEP_EXECUTE && executeState === 'idle' && (
              <>
                <button type="button" onClick={handleExecuteNext}
                  className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2">
                  Skip
                </button>
                <button
                  type="button"
                  onClick={handleExecuteTrade}
                  className={`px-5 py-2.5 text-sm font-bold text-white rounded-md transition-all ${
                    recommendation?.action === 'SHORT'
                      ? 'bg-red-600 hover:bg-red-500 shadow-[0_0_16px_rgba(239,68,68,0.3)] hover:shadow-[0_0_24px_rgba(239,68,68,0.5)]'
                      : 'bg-emerald-600 hover:bg-emerald-500 shadow-[0_0_16px_rgba(16,185,129,0.3)] hover:shadow-[0_0_24px_rgba(16,185,129,0.5)]'
                  }`}
                >
                  {recommendation?.action === 'SHORT' ? '↓ Short' : '↑ Long'} {recommendation?.pair} →
                </button>
              </>
            )}
            {step === STEP_EXECUTE && executeState === 'executing' && (
              <button type="button" disabled className="px-5 py-2.5 text-sm font-bold text-white bg-slate-700 rounded-md opacity-60 cursor-not-allowed">
                Placing order…
              </button>
            )}
            {step === STEP_EXECUTE && executeState === 'done' && (
              <button type="button" onClick={handleExecuteNext}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md transition-colors">
                Continue →
              </button>
            )}
            {step === STEP_EXECUTE && executeState === 'error' && (
              <>
                <button type="button" onClick={handleExecuteNext}
                  className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2">
                  Skip
                </button>
                <button type="button" onClick={() => { setExecuteState('idle'); setExecuteError(null); }}
                  className="px-4 py-2 text-sm font-medium text-white bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-md transition-colors">
                  Retry
                </button>
              </>
            )}

            {/* Standard next buttons */}
            {step === STEP_PORTFOLIO && (
              <button type="button" onClick={handlePortfolioContinue} disabled={!selectedPortfolioId || busy}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md disabled:opacity-50 transition-colors">
                Continue →
              </button>
            )}
            {step === STEP_IDENTITY && (
              <button type="button" onClick={handleIdentityContinue} disabled={busy || !SLUG_RE.test(toSlug(agentName.trim()))}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md disabled:opacity-50 transition-colors">
                {busy ? 'Creating…' : 'Continue →'}
              </button>
            )}
            {step === STEP_STRATEGY && (
              <button type="button" onClick={handleStrategyContinue} disabled={!selectedPreset || busy}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md disabled:opacity-50 transition-colors">
                {busy ? 'Saving…' : 'Continue →'}
              </button>
            )}
            {step === STEP_DONE && (
              <button type="button" onClick={() => {
                handleClose();
                if (tradeMade === true && selectedPortfolioId) navigate(`/workspace/${selectedPortfolioId}`);
              }}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md transition-colors">
                {tradeMade === true ? 'Go to Workspace →' : 'Go to Dashboard →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
